/**
 * Custom Invoice — type the lines, HQ makes the Stripe payment link.
 *
 * Opened from the "Custom Invoice" button on the Saved Quotes page, and from
 * any invoice card there (which reopens it in view mode). Creating one saves
 * it as a quote, so it lives in the same shared pool as every other quote.
 *
 * Every line is always an editable row and "Add line" just adds another
 * empty one — there is no staging row that has to be "+"-ed in, because a row
 * the rep typed but never committed is exactly how lines go missing.
 *
 * Opening a pending invoice asks Stripe whether it has been paid, so the
 * status a rep sees here is Stripe's, not a guess from a webhook that may
 * not have arrived.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X,
  Loader2,
  CheckCircle2,
  Copy,
  Check,
  Plus,
  Trash2,
  CreditCard,
  ExternalLink,
  RefreshCw,
  Ban,
  Receipt,
} from "lucide-react";
import { apiClient } from "../../App";

// Whole percent, so fee math stays in integers (0.03 * 100 is not 3 in JS).
const CARD_FEE_PERCENT = 3;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmtUSD = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const emptyLine = () => ({ key: Math.random().toString(36).slice(2), description: "", quantity: "1", unit_price: "" });

// Cents, so the total on screen is the total Stripe will charge.
const cents = (v) => Math.round((parseFloat(String(v).replace(/[$,\s]/g, "")) || 0) * 100);
const qtyOf = (v) => Math.max(0, parseInt(v, 10) || 0);

export const invoiceStatus = (q) => {
  const s = q?.stripe_payment_status;
  if (s === "paid") return { id: "paid", label: "Paid", color: "#3ECF83", bg: "rgba(10,138,74,0.14)", border: "rgba(60,201,122,0.35)" };
  if (s === "deactivated") return { id: "deactivated", label: "Link off", color: "rgba(255,255,255,0.45)", bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.12)" };
  return { id: "pending", label: "Awaiting payment", color: "#E0B36B", bg: "rgba(224,179,107,0.12)", border: "rgba(224,179,107,0.35)" };
};

export function InvoiceStatusPill({ quote }) {
  const s = invoiceStatus(quote);
  return (
    <span
      style={{
        fontSize: "10px", letterSpacing: "0.16em", textTransform: "uppercase",
        padding: "3px 9px", borderRadius: "999px", whiteSpace: "nowrap",
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      }}
      data-testid={`invoice-status-${s.id}`}
    >
      {s.label}
    </span>
  );
}

function CopyLink({ url, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — select the link and copy it by hand");
    }
  };
  return (
    <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-lg px-3.5 py-3">
      <CreditCard className="w-4 h-4 shrink-0 text-[#8FA79B]" />
      <div className="min-w-0 flex-1">
        <p className="text-white/80" style={{ fontSize: "12px" }}>Stripe payment link</p>
        <p className="truncate text-white/35" style={{ fontSize: "11px" }} title={url}>{url}</p>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 p-1.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors"
        title="Open the link"
        data-testid={`${testId}-open`}
      >
        <ExternalLink className="w-3 h-3" />
      </a>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors"
        style={{ fontSize: "11px" }}
        data-testid={testId}
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-white/55 mb-1.5" style={{ fontSize: "11.5px" }}>{label}</span>
      {children}
      {hint ? <span className="block text-white/30 mt-1.5" style={{ fontSize: "11px" }}>{hint}</span> : null}
    </label>
  );
}

const inputClass =
  "w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#8FA79B] transition-colors";

/** The itemized invoice, read-only — what the client sees on Stripe's page. */
function InvoiceLines({ invoice }) {
  const lines = invoice?.line_items || [];
  return (
    <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="invoice-lines">
      {lines.map((ln, i) => (
        <div key={i} className="flex items-baseline justify-between gap-4 px-3.5 py-2.5 border-b border-white/[0.06]">
          <p className="text-white/85 min-w-0" style={{ fontSize: "12.5px" }}>
            {ln.description}
            {ln.quantity > 1 ? (
              <span className="text-white/35"> · {ln.quantity} × {fmtUSD(ln.unit_price)}</span>
            ) : null}
          </p>
          <p className="text-white/85 shrink-0" style={{ fontSize: "12.5px", fontVariantNumeric: "tabular-nums" }}>
            {fmtUSD(ln.amount)}
          </p>
        </div>
      ))}
      {invoice?.card_fee > 0 && (
        <div className="flex items-baseline justify-between gap-4 px-3.5 py-2.5 border-b border-white/[0.06]">
          <p className="text-white/50" style={{ fontSize: "12.5px" }}>Credit card processing fee (3%)</p>
          <p className="text-white/50 shrink-0" style={{ fontSize: "12.5px", fontVariantNumeric: "tabular-nums" }}>
            {fmtUSD(invoice.card_fee)}
          </p>
        </div>
      )}
      <div className="flex items-baseline justify-between gap-4 px-3.5 py-3 bg-white/[0.03]">
        <p className="text-white" style={{ fontSize: "13px" }}>Total</p>
        <p className="text-white shrink-0" style={{ fontSize: "15px", fontVariantNumeric: "tabular-nums" }} data-testid="invoice-total">
          {fmtUSD(invoice?.total)}
        </p>
      </div>
    </div>
  );
}

export default function CustomInvoiceModal({ open, onClose, invoice = null, onSaved }) {
  const [clientName, setClientName] = useState("");
  const [company, setCompany] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [memo, setMemo] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [addCardFee, setAddCardFee] = useState(true);
  const [creating, setCreating] = useState(false);
  // The invoice on screen once one exists — a fresh create, or the one the
  // rep opened from its card.
  const [current, setCurrent] = useState(null);
  const [checking, setChecking] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClientName("");
    setCompany("");
    setClientEmail("");
    setMemo("");
    setLines([emptyLine()]);
    setAddCardFee(true);
    setCreating(false);
    setConfirmOff(false);
    setCurrent(invoice);
  }, [open, invoice]);

  // Ask Stripe on open — the only way a pending invoice shows "Paid" when the
  // webhook never came.
  const invoiceId = invoice?.id;
  const invoicePending = invoice && invoiceStatus(invoice).id === "pending";
  useEffect(() => {
    if (!open || !invoiceId || !invoicePending) return;
    let alive = true;
    (async () => {
      setChecking(true);
      try {
        const { data } = await apiClient.post(`/custom-invoices/${invoiceId}/refresh`);
        if (!alive) return;
        setCurrent(data);
        if (data?.stripe_payment_status === "paid") onSaved?.(data);
      } catch {
        // Silent: the stored status still shows, and the button can retry.
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId, invoicePending]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !creating) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, creating, onClose]);

  const totals = useMemo(() => {
    const subtotalC = lines.reduce((s, ln) => s + cents(ln.unit_price) * qtyOf(ln.quantity), 0);
    // Integer math (×3 ÷100), so a half cent is exactly .5 and rounds up like the server.
    const feeC = addCardFee ? Math.round((subtotalC * CARD_FEE_PERCENT) / 100) : 0;
    return { subtotal: subtotalC / 100, fee: feeC / 100, total: (subtotalC + feeC) / 100 };
  }, [lines, addCardFee]);

  // A line counts once it has a description or a price; a fully blank row is
  // just the spare one and is ignored rather than blocking the create.
  const touched = lines.filter((ln) => ln.description.trim() || String(ln.unit_price).trim());
  const lineErrors = touched.filter(
    (ln) => !ln.description.trim() || cents(ln.unit_price) <= 0 || qtyOf(ln.quantity) < 1
  );
  const emailOk = !clientEmail.trim() || EMAIL_RE.test(clientEmail.trim());
  const canCreate =
    !creating && clientName.trim() && touched.length > 0 && lineErrors.length === 0 &&
    emailOk && totals.total >= 0.5;

  const updateLine = (key, patch) =>
    setLines((ls) => ls.map((ln) => (ln.key === key ? { ...ln, ...patch } : ln)));
  const removeLine = (key) =>
    setLines((ls) => (ls.length === 1 ? [emptyLine()] : ls.filter((ln) => ln.key !== key)));

  const create = async (e) => {
    e?.preventDefault?.();
    if (!canCreate) return;
    setCreating(true);
    try {
      const { data } = await apiClient.post("/custom-invoices", {
        client_name: clientName.trim(),
        client_entity: company.trim(),
        client_email: clientEmail.trim(),
        memo: memo.trim(),
        add_card_fee: addCardFee,
        line_items: touched.map((ln) => ({
          description: ln.description.trim(),
          quantity: qtyOf(ln.quantity),
          unit_price: cents(ln.unit_price) / 100,
        })),
      });
      setCurrent(data);
      onSaved?.(data);
      toast.success("Payment link created and saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't create the payment link");
    } finally {
      setCreating(false);
    }
  };

  const checkPayment = async () => {
    if (!current) return;
    setChecking(true);
    try {
      const { data } = await apiClient.post(`/custom-invoices/${current.id}/refresh`);
      setCurrent(data);
      onSaved?.(data);
      toast[data?.stripe_payment_status === "paid" ? "success" : "message"](
        data?.stripe_payment_status === "paid" ? "Paid" : "Not paid yet"
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't check with Stripe");
    } finally {
      setChecking(false);
    }
  };

  const deactivate = async () => {
    if (!current) return;
    setDeactivating(true);
    try {
      const { data } = await apiClient.post(`/custom-invoices/${current.id}/deactivate`);
      setCurrent(data);
      onSaved?.(data);
      toast.success("Link turned off — the client can no longer pay it");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't turn the link off");
      // A 409 means the client paid in the meantime; show that.
      if (err?.response?.status === 409) checkPayment();
    } finally {
      setDeactivating(false);
      setConfirmOff(false);
    }
  };

  if (!open) return null;

  const status = current ? invoiceStatus(current) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      onClick={() => { if (!creating) onClose(); }}
      data-testid="custom-invoice-modal"
    >
      <div
        className="relative w-full max-w-[640px] my-6 bg-[#080808] border border-white/12 rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 sm:px-7 pt-6 pb-5 border-b border-white/[0.07]">
          <div className="min-w-0">
            <p className="uppercase text-[#8FA79B] mb-2" style={{ fontSize: "10px", letterSpacing: "0.22em" }}>
              Custom Invoice
            </p>
            <h2
              className="text-white font-light truncate"
              style={{ fontFamily: "var(--font-sans)", fontSize: "22px", letterSpacing: "-0.3px" }}
            >
              {current ? current.client_name : "New payment link"}
            </h2>
            <p className="text-white/40 mt-1.5" style={{ fontSize: "12.5px" }}>
              {current
                ? [current.client_entity, `Created ${fmtWhen(current.created_at)}`, current.created_by_name]
                    .filter(Boolean).join(" · ")
                : "Type the lines and prices — HQ makes the Stripe link. No Stripe login needed."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => { if (!creating) onClose(); }}
            className="shrink-0 text-white/40 hover:text-white transition-colors p-1 -mt-1 -mr-1"
            aria-label="Close"
            data-testid="custom-invoice-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {current ? (
          /* ── Invoice ──────────────────────────────────────────────── */
          <div className="px-6 sm:px-7 py-6" data-testid="custom-invoice-view">
            <div className="flex items-center gap-2.5 mb-5">
              {status.id === "paid" ? (
                <CheckCircle2 className="w-5 h-5 text-[#3ECF83] shrink-0" />
              ) : (
                <Receipt className="w-5 h-5 text-white/50 shrink-0" />
              )}
              <p className="text-white flex-1" style={{ fontSize: "14px" }}>
                {status.id === "paid"
                  ? `Paid ${fmtUSD(current.invoice?.amount_paid ?? current.invoice?.total)}${current.stripe_paid_at ? ` on ${fmtWhen(current.stripe_paid_at)}` : ""}`
                  : status.id === "deactivated"
                  ? "Link turned off — this invoice can't be paid"
                  : "Waiting for the client to pay"}
              </p>
              <InvoiceStatusPill quote={current} />
            </div>

            <InvoiceLines invoice={current.invoice} />

            {current.invoice?.memo ? (
              <p className="text-white/40 mt-3" style={{ fontSize: "11.5px" }}>
                Note (only HQ sees this): {current.invoice.memo}
              </p>
            ) : null}

            {status.id === "pending" && current.stripe_payment_link && (
              <div className="mt-5">
                <CopyLink url={current.stripe_payment_link} testId="custom-invoice-copy-link" />
                <p className="text-white/35 mt-2" style={{ fontSize: "11px" }}>
                  The link doesn't expire, and it closes itself after one payment.
                </p>
              </div>
            )}

            {confirmOff && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <p className="text-red-300 text-[12px]">Turn the link off? The client won't be able to pay it.</p>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setConfirmOff(false)} disabled={deactivating}
                    className="px-3 py-1 rounded text-[11px] text-white/65 hover:text-white disabled:opacity-50">
                    Cancel
                  </button>
                  <button type="button" onClick={deactivate} disabled={deactivating}
                    className="px-3 py-1 rounded text-[11px] bg-red-500/80 hover:bg-red-500 text-white disabled:opacity-60"
                    data-testid="custom-invoice-deactivate-yes">
                    {deactivating ? "Turning off…" : "Yes, turn it off"}
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2.5 mt-6">
              {status.id === "pending" && (
                <button
                  type="button"
                  onClick={checkPayment}
                  disabled={checking}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white/75 hover:border-white/30 hover:text-white transition-colors disabled:opacity-50"
                  style={{ fontSize: "12.5px" }}
                  data-testid="custom-invoice-check"
                >
                  {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {checking ? "Checking Stripe…" : "Check payment"}
                </button>
              )}
              {status.id === "pending" && !confirmOff && (
                <button
                  type="button"
                  onClick={() => setConfirmOff(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[#D46A4C] hover:bg-[#D46A4C]/10 transition-colors"
                  style={{ fontSize: "12.5px" }}
                  data-testid="custom-invoice-deactivate"
                >
                  <Ban className="w-4 h-4" />
                  Turn link off
                </button>
              )}
              <div className="flex-1" />
              {!invoice && (
                <button
                  type="button"
                  onClick={() => { setCurrent(null); setLines([emptyLine()]); setClientName(""); setCompany(""); setClientEmail(""); setMemo(""); }}
                  className="px-5 py-2.5 rounded-full border border-white/15 text-white/70 hover:border-white/30 hover:text-white transition-colors"
                  style={{ fontSize: "12.5px" }}
                  data-testid="custom-invoice-another"
                >
                  New invoice
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-full transition-all"
                style={{ background: "#8FA79B", color: "#000000", fontSize: "12.5px" }}
                data-testid="custom-invoice-done"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* ── Form ─────────────────────────────────────────────────── */
          <form onSubmit={create} className="px-6 sm:px-7 py-6" data-testid="custom-invoice-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Client name">
                <input autoFocus value={clientName} onChange={(e) => setClientName(e.target.value)}
                  placeholder="Jordan Reyes" className={inputClass} data-testid="custom-invoice-client-name" />
              </Field>
              <Field label="Company (optional)">
                <input value={company} onChange={(e) => setCompany(e.target.value)}
                  placeholder="Solara Development Group" className={inputClass} data-testid="custom-invoice-company" />
              </Field>
              <div className="sm:col-span-2">
                <Field
                  label="Client email (optional)"
                  hint={!emailOk ? "That does not look like an email address." : "For your records — HQ doesn't email the link. Copy it and send it yourself."}
                >
                  <input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="jordan@example.com" className={inputClass} data-testid="custom-invoice-client-email" />
                </Field>
              </div>
            </div>

            {/* Line items */}
            <p className="uppercase text-white/35 mt-6 mb-2.5" style={{ fontSize: "10px", letterSpacing: "0.2em" }}>
              What you're billing
            </p>
            <div className="space-y-2" data-testid="custom-invoice-lines">
              <div className="hidden sm:grid grid-cols-[1fr_64px_120px_28px] gap-2 px-0.5 text-white/35" style={{ fontSize: "10.5px" }}>
                <span>Description</span><span>Qty</span><span>Price each</span><span />
              </div>
              {lines.map((ln, i) => (
                // Phone: description gets its own full-width row, qty/price under it.
                <div key={ln.key} className="grid grid-cols-[64px_1fr_28px] sm:grid-cols-[1fr_64px_120px_28px] gap-2 items-center pb-2 sm:pb-0 border-b sm:border-0 border-white/[0.06]">
                  <input
                    value={ln.description}
                    onChange={(e) => updateLine(ln.key, { description: e.target.value })}
                    placeholder={i === 0 ? "e.g. 2 additional exterior renderings" : "Description"}
                    className={`${inputClass} col-span-3 sm:col-span-1`}
                    data-testid={`custom-invoice-line-desc-${i}`}
                  />
                  <input
                    inputMode="numeric"
                    value={ln.quantity}
                    onChange={(e) => updateLine(ln.key, { quantity: e.target.value.replace(/[^\d]/g, "") })}
                    className={`${inputClass} text-center`}
                    aria-label="Quantity"
                    data-testid={`custom-invoice-line-qty-${i}`}
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35 text-[13px]">$</span>
                    <input
                      inputMode="decimal"
                      value={ln.unit_price}
                      onChange={(e) => updateLine(ln.key, { unit_price: e.target.value.replace(/[^\d.,]/g, "") })}
                      placeholder="0.00"
                      className={`${inputClass} pl-6 text-right`}
                      aria-label="Price each"
                      data-testid={`custom-invoice-line-price-${i}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(ln.key)}
                    className="p-1.5 text-white/30 hover:text-[#D46A4C] transition-colors"
                    aria-label="Remove line"
                    data-testid={`custom-invoice-line-remove-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setLines((ls) => [...ls, emptyLine()])}
                disabled={lines.length >= 40}
                className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-full border border-white/12 text-white/65 hover:text-white hover:border-white/30 transition-colors disabled:opacity-40"
                style={{ fontSize: "11.5px" }}
                data-testid="custom-invoice-add-line"
              >
                <Plus className="w-3.5 h-3.5" /> Add line
              </button>
              {lineErrors.length > 0 && (
                <p className="text-[#E0B36B]" style={{ fontSize: "11px" }}>
                  Every line needs a description, a quantity of at least 1, and a price.
                </p>
              )}
            </div>

            {/* Totals */}
            <div className="mt-5 rounded-lg border border-white/10 px-4 py-3 space-y-1.5" style={{ fontVariantNumeric: "tabular-nums" }}>
              <div className="flex justify-between text-white/60" style={{ fontSize: "12.5px" }}>
                <span>Subtotal</span><span>{fmtUSD(totals.subtotal)}</span>
              </div>
              <label className="flex items-center justify-between gap-3 cursor-pointer text-white/60" style={{ fontSize: "12.5px" }}>
                <span className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={addCardFee} onChange={(e) => setAddCardFee(e.target.checked)}
                    className="accent-[#8FA79B]" data-testid="custom-invoice-card-fee" />
                  Add the 3% card processing fee
                </span>
                <span>{addCardFee ? fmtUSD(totals.fee) : "—"}</span>
              </label>
              <div className="flex justify-between pt-1.5 border-t border-white/[0.07] text-white" style={{ fontSize: "14px" }}>
                <span>Client pays</span>
                <span data-testid="custom-invoice-form-total">{fmtUSD(totals.total)}</span>
              </div>
            </div>

            <div className="mt-4">
              <Field label="Note for HQ (optional)" hint="Saved with the invoice. The client never sees it.">
                <input value={memo} onChange={(e) => setMemo(e.target.value)}
                  placeholder="e.g. Upsell agreed on the 10/3 call" className={inputClass} data-testid="custom-invoice-memo" />
              </Field>
            </div>

            <div className="flex flex-wrap items-center gap-2.5 mt-6">
              <button
                type="submit"
                disabled={!canCreate}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#8FA79B", color: "#000000", fontSize: "12.5px" }}
                data-testid="custom-invoice-submit"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {creating ? "Creating link…" : "Create payment link"}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={creating}
                className="px-5 py-2.5 rounded-full border border-white/15 text-white/60 hover:border-white/30 hover:text-white transition-colors disabled:opacity-40"
                style={{ fontSize: "12.5px" }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
